// src/components/admin/blog/PostForm.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Eye, Image, Film, Upload, X, CheckCircle, AlertTriangle,
  ArrowLeft, Trash2, Plus, Sparkles, RefreshCw, Wand, Settings
} from 'lucide-react';
import RichTextEditor from '../shared/RichTextEditor';
import NotificationBar from '../shared/NotificationBar';
import MediaUploader from '../shared/MediaUploader';
import AiSettingsModal from '../shared/AiSettingsModal';

const PostForm = () => {
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [originalPost, setOriginalPost] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const [postForm, setPostForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    coverImage: '',
    category: '',
    tags: [],
    status: 'draft',
    featured: false,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    images: [],
    videos: [],
  });
  
  // For handling content changes from rich text editor
  const [editorContent, setEditorContent] = useState('');
  
  // Tabs for organizing the form
  const [activeTab, setActiveTab] = useState('content');
  
  // Track if form is dirty (has unsaved changes)
  const [isDirty, setIsDirty] = useState(false);
  
  // Refs for file input elements
  const coverImageInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const navigate = useNavigate();
  const params = useParams();
  const postId = params.id;

  const [showAiSettings, setShowAiSettings] = useState(false);
  const [aiSettings, setAiSettings] = useState({
    contentLength: 'medium',
    tone: 'professional',
    targetAudience: 'general',
    customInstructions: '',
  });
  
  useEffect(() => {
    // Fetch categories and tags
    fetchCategoriesAndTags();
    
    // If we have a postId, we're editing an existing post
    if (postId) {
      setIsEditing(true);
      fetchPost(postId);
    }
    
    // Setup confirmation before leaving page with unsaved changes
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [postId]);
  
  // Update form dirty state when form changes
  useEffect(() => {
    if (originalPost) {
      const hasChanges = JSON.stringify(originalPost) !== JSON.stringify({
        ...postForm,
        content: editorContent
      });
      setIsDirty(hasChanges);
    } else {
      // For new posts, check if any fields have been filled out
      const isEmpty = !postForm.title && !postForm.excerpt && !editorContent;
      setIsDirty(!isEmpty);
    }
  }, [postForm, editorContent, originalPost]);
  

  // Function to generate blog content using AI
  const generateAIContent = async () => {
    if (!postForm.title) {
      showNotification('error', 'Please add a title first');
      return;
    }
  
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Include the AI settings in the request
      const requestBody = {
        topic: postForm.title,
        keywords: postForm.seoKeywords,
        settings: aiSettings 
      };
      
      const res = await fetch('/api/blog-ai/generate-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to generate content');
      }
      
      const data = await res.json();
      
      // Set the editor content with the AI-generated content
      setEditorContent(data.content);
      // Update the form content field
      setPostForm(prev => ({ ...prev, content: data.content }));
      
      showNotification('success', 'AI content generated successfully');
    } catch (error) {
      console.error('Error generating AI content:', error);
      let errorMessage = 'Failed to generate content';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.details || error.response.data.error || errorMessage;
      }
      
      showNotification('error', errorMessage);
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleApplyAiSettings = (newSettings) => {
    setAiSettings(newSettings);
    showNotification('success', 'AI settings applied');
  };

  // Function to generate SEO metadata using AI
  const generateAISeo = async () => {
    if (!postForm.title || !editorContent) {
      showNotification('error', 'Title and content are required for SEO generation');
      return;
    }

    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/blog-ai/generate-seo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: postForm.title,
          content: editorContent
        })
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to generate SEO data');
      }
      
      const data = await res.json();
      
      // Update the form with the AI-generated SEO data
      setPostForm(prev => ({
        ...prev,
        seoTitle: data.seoTitle,
        seoDescription: data.metaDescription
      }));
      
      showNotification('success', 'AI SEO metadata generated successfully');
    } catch (error) {
      console.error('Error generating AI SEO data:', error);
      showNotification('error', error.message || 'Failed to generate SEO data');
    } finally {
      setAiLoading(false);
    }
  };

  const validateForm = () => {
    const errors = [];    
    if (!postForm.title) {
      errors.push('Title is required');
    }    
    if (!postForm.excerpt) {
      errors.push('Description is required');
    }    
    if (!editorContent) {
      errors.push('Content is required');
    }    
    if (!postForm.category) {
      errors.push('Category is required');
    }    
    return errors;
  };

  const fetchCategoriesAndTags = async () => {
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        fetch('/api/blog/categories'),
        fetch('/api/blog/tags')
      ]);
      
      if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
      if (!tagsRes.ok) throw new Error('Failed to fetch tags');
      
      const categoriesData = await categoriesRes.json();
      const tagsData = await tagsRes.json();
      
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (error) {
      console.error('Error fetching form data:', error);
      showNotification('error', 'Failed to load form data');
    }
  };
  
  // const fetchPost = async (id) => {
  //   setLoadingPost(true);
  //   try {
  //     const token = localStorage.getItem('token');
  //     const res = await fetch(`/api/blog/posts/${id}`, {
  //       headers: {
  //         Authorization: `Bearer ${token}`
  //       }
  //     });
      
  //     if (!res.ok) throw new Error('Failed to fetch post');
      
  //     const post = await res.json();
      
  //     // Set the form state with the post data
  //     const formData = {
  //       title: post.title,
  //       excerpt: post.excerpt || '',
  //       content: post.content,
  //       coverImage: post.coverImage || '',
  //       category: post.category?._id || '',
  //       tags: post.tags?.map(tag => tag._id) || [],
  //       status: post.status || 'draft',
  //       featured: post.featured || false,
  //       seoTitle: post.seoTitle || '',
  //       seoDescription: post.seoDescription || '',
  //       seoKeywords: post.seoKeywords || '',
  //       images: post.images || [],
  //       videos: post.videos || []
  //     };
      
  //     setPostForm(formData);
  //     setEditorContent(post.content);
  //     setOriginalPost(formData);
  //   } catch (error) {
  //     console.error('Error fetching post:', error);
  //     showNotification('error', 'Failed to load post');
  //   } finally {
  //     setLoadingPost(false);
  //   }
  // };
 
  const fetchPost = async (id) => {
    setLoadingPost(true);
    try {
      const token = localStorage.getItem('token');
      // Change the URL to use the new route with /id/ prefix
      const res = await fetch(`/api/blog/posts/id/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch post');
      
      const post = await res.json();
      
      // Set the form state with the post data
      const formData = {
        title: post.title,
        excerpt: post.excerpt || '',
        content: post.content,
        coverImage: post.coverImage || '',
        category: post.category?._id || '',
        tags: post.tags?.map(tag => tag._id) || [],
        status: post.status || 'draft',
        featured: post.featured || false,
        seoTitle: post.seoTitle || '',
        seoDescription: post.seoDescription || '',
        seoKeywords: post.seoKeywords || '',
        images: post.images || [],
        videos: post.videos || []
      };
      
      setPostForm(formData);
      setEditorContent(post.content);
      setOriginalPost(formData);
    } catch (error) {
      console.error('Error fetching post:', error);
      showNotification('error', 'Failed to load post');
    } finally {
      setLoadingPost(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPostForm(prev => ({ ...prev, [name]: value }));
  };
  
  const handleTagsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setPostForm(prev => ({ ...prev, tags: selectedOptions }));
  };
  
  const handleEditorChange = (content) => {
    setEditorContent(content);
    // Also update the postForm content field to keep it in sync
    setPostForm(prev => ({ ...prev, content }));
  };
  
  // const handleSubmit = async (e, publishAfterSave = false) => {
  //   e.preventDefault();
  //   setLoading(true);
    
  //   try {
  //     const token = localStorage.getItem('token');
  //     const method = isEditing ? 'PUT' : 'POST';
  //     const url = isEditing 
  //       ? `/api/blog/posts/${postId}`
  //       : '/api/blog/posts';
      
  //     // If publishing, override the status
  //     const formData = {
  //       ...postForm,
  //       content: editorContent,
  //       status: publishAfterSave ? 'published' : postForm.status
  //     };
      
  //     const res = await fetch(url, {
  //       method,
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${token}`
  //       },
  //       body: JSON.stringify(formData)
  //     });
      
  //     if (!res.ok) {
  //       const errorData = await res.json();
  //       throw new Error(errorData.error || 'Failed to save post');
  //     }
      
  //     const data = await res.json();
      
  //     // Update original post to reset dirty state
  //     setOriginalPost(formData);
  //     setIsDirty(false);
      
  //     showNotification('success', `Post ${isEditing ? 'updated' : 'created'} successfully`);
      
  //     // Redirect to posts list after short delay
  //     setTimeout(() => {
  //       navigate('/admin/blog/posts');
  //     }, 1500);
  //   } catch (error) {
  //     console.error('Error saving post:', error);
  //     showNotification('error', error.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  // Replace or modify the handleSubmit function in PostForm.js
  // In handleSubmit function in PostForm.js
  const handleSubmit = async (e, publishAfterSave = false) => {
    e.preventDefault();
    
    // Validate form before submission
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showNotification('error', `Please fix the following errors: ${validationErrors.join(', ')}`);
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing 
        ? `/api/blog/posts/${postId}`
        : '/api/blog/posts';
      
      // If publishing, override the status
      const formData = {
        ...postForm,
        content: editorContent,
        status: publishAfterSave ? 'published' : postForm.status
      };

      console.log('Submitting form data:', JSON.stringify(formData)); // Better debugging
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Server response:', errorText);
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to save post');
      }
      
      const data = await res.json();
      
      // Update original post to reset dirty state
      setOriginalPost(formData);
      setIsDirty(false);
      
      showNotification('success', `Post ${isEditing ? 'updated' : 'created'} successfully`);
      
      // Redirect to posts list after short delay
      setTimeout(() => {
        navigate('/admin/blog/posts');
      }, 1500);
    } catch (error) {
      console.error('Error saving post:', error);
      showNotification('error', error.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
    } finally {
      setLoading(false);
    }
  };
   
  

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    // Check file size before uploading
    const maxSizeInBytes = 50 * 1024 * 1024; // 50MB (slightly under server limit)
    if (file.size > maxSizeInBytes) {
      showNotification('error', `File size exceeds limit. Maximum size is 50MB.`);
      return null;
    }
    
    // Create formData
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      
      // Display progress notification
      showNotification('info', `Uploading ${type === 'video' ? 'video' : 'image'}...`, 0); // 0 means don't auto-close
      
      // Show upload progress in notification
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2-minute timeout
      
      const res = await fetch('/api/blog/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Clear the progress notification
      setNotification(null);
      
      if (!res.ok) {
        // Try to parse error as JSON
        let errorData;
        const contentType = res.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          errorData = await res.json();
        } else {
          // Handle non-JSON response
          const text = await res.text();
          throw new Error(`Server returned status ${res.status}: ${text.substring(0, 100)}...`);
        }
        
        throw new Error(errorData.error || 'Failed to upload file');
      }
      
      const data = await res.json();
      
      // Update form based on file type
      if (type === 'cover') {
        setPostForm(prev => ({
          ...prev,
          coverImage: data.filePath
        }));
      } else if (type === 'image') {
        setPostForm(prev => ({
          ...prev,
          images: [...prev.images, data.filePath]
        }));
      } else if (type === 'video') {
        setPostForm(prev => ({
          ...prev,
          videos: [...prev.videos, data.filePath]
        }));
      }
      
      showNotification('success', `${type === 'video' ? 'Video' : 'Image'} uploaded successfully`);
      return data.filePath;
    } catch (error) {
      console.error('Error uploading file:', error);
      showNotification('error', error.message || 'Failed to upload file. Please try again with a smaller file.');
      return null;
    }
  };
  
  // Helper function to format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const handleRemoveMedia = (type, index) => {
    if (type === 'cover') {
      setPostForm(prev => ({
        ...prev,
        coverImage: ''
      }));
    } else if (type === 'image') {
      setPostForm(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    } else if (type === 'video') {
      setPostForm(prev => ({
        ...prev,
        videos: prev.videos.filter((_, i) => i !== index)
      }));
    }
  };
  
  const showNotification = (type, message, duration = 5000) => {
    setNotification({ type, message });
    if (duration) {
      setTimeout(() => setNotification(null), duration);
    }
  };
  
  // Generate SEO fields from content
  const generateSeoFields = () => {
    // If title is empty, don't try to generate SEO fields
    if (!postForm.title) {
      showNotification('error', 'Please add a title first');
      return;
    }
    
    // Generate SEO title if empty
    if (!postForm.seoTitle) {
      setPostForm(prev => ({
        ...prev,
        seoTitle: prev.title
      }));
    }
    
    // Generate SEO description from excerpt or content
    if (!postForm.seoDescription) {
      let description = '';
      
      if (postForm.excerpt) {
        description = postForm.excerpt;
      } else if (editorContent) {
        // Strip HTML tags and get first 160 characters
        const plainText = editorContent.replace(/<[^>]*>?/gm, '');
        description = plainText.substring(0, 160).trim();
        
        // Add ellipsis if truncated
        if (plainText.length > 160) {
          description += '...';
        }
      }
      
      setPostForm(prev => ({
        ...prev,
        seoDescription: description
      }));
    }
    
    // Generate keywords from title and content
    if (!postForm.seoKeywords) {
      // Extract key terms from title and content
      const allText = `${postForm.title} ${editorContent.replace(/<[^>]*>?/gm, '')}`;
      
      // Split into words, filter out short words and common stop words
      const stopWords = ['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'of'];
      const words = allText.toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(/\s+/)
        .filter(word => word.length > 3 && !stopWords.includes(word));
      
      // Count frequency
      const wordCounts = {};
      words.forEach(word => {
        wordCounts[word] = (wordCounts[word] || 0) + 1;
      });
      
      // Get top 5-8 most frequent words
      const keywords = Object.entries(wordCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(entry => entry[0])
        .join(', ');
      
      setPostForm(prev => ({
        ...prev,
        seoKeywords: keywords
      }));
    }
    
    showNotification('success', 'SEO fields generated');
  };
  
  if (loadingPost) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center items-center h-64">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-2 text-gray-500">Loading post...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white pt-4 rounded-lg shadow-md">
      {notification && (
        <NotificationBar
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      <div className="px-1 pr-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => {
                if (isDirty) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                    navigate('/admin/blog/posts');
                  }
                } else {
                  navigate('/admin/blog/posts');
                }
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit Post' : 'Create New Post'}
            </h2>
          </div>
          
          <div className="flex items-center">
            <span className="mr-2 text-sm text-gray-500">Status:</span>
            <select
              name="status"
              value={postForm.status}
              onChange={handleInputChange}
              className="border border-gray-300 rounded-md text-sm py-1 px-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-end mt-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => handleSubmit(e, false)}
              disabled={loading}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </button>
            
            <button
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {postForm.status === 'published' ? 'Update' : 'Publish'}
            </button>
            
            <button
              onClick={() => window.open(`/blog/${originalPost?.slug}`, '_blank')}
              disabled={!isEditing || postForm.status !== 'published'}
              className={`px-4 py-2 rounded-md flex items-center ${
                isEditing && postForm.status === 'published'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </button>
          </div>
        </div>
      </div>
      
      {/* Post Form */}
      <div className="p-6">
        <form onSubmit={(e) => handleSubmit(e, false)}>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <div className="flex -mb-px space-x-8">
              <button
                type="button"
                onClick={() => setActiveTab('content')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'content'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Content
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('media')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'media'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Media
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('seo')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'seo'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                SEO
              </button>
            </div>
          </div>
          
          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={postForm.title}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Post title"
                />
                  <p className="mt-1 text-sm text-gray-500">
                  {postForm.title.length} / 55 characters recommended
                </p>
              </div>
              
              <div>
                <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                 Description
                </label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  value={postForm.excerpt}
                  onChange={handleInputChange}
                  rows="3"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description (shown in previews)"
                ></textarea>
                <p className="mt-1 text-sm text-gray-500">
                  {postForm.excerpt.length} / 160 characters recommended
                </p>
              </div>
              
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  required
                  value={postForm.category}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <select
                  id="tags"
                  name="tags"
                  multiple
                  value={postForm.tags}
                  onChange={handleTagsChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 h-32"
                >
                  {tags.map(tag => (
                    <option key={tag._id} value={tag._id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Hold Ctrl (or Command on Mac) to select multiple tags
                </p>
              </div>

              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={postForm.featured}
                  onChange={(e) => setPostForm(prev => ({ ...prev, featured: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                  Feature this post
                </label>
              </div>
              

              {/* AI Content Generation Button - Add this right above the RichTextEditor component */}
              <div className="flex justify-end mb-2 space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAiSettings(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  AI Settings
                </button>
                
                <button
                  type="button"
                  onClick={generateAIContent}
                  disabled={aiLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                >
                  {aiLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate AI Content
                    </>
                  )}
                </button>
              </div>

              

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <RichTextEditor 
                  initialContent={editorContent}
                  onChange={handleEditorChange}
                  placeholder="Write your post content here..."
                />
              </div>
            </div>
          )}
          
          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Cover Image</h3>
                <div className="border border-blue-300 rounded-md bg-blue-50 shadow-sm overflow-hidden">
                  {postForm.coverImage ? (
                    <div className="relative">
                      <img
                        src={postForm.coverImage}
                        alt="Cover"
                        className="w-full h-64 object-cover"
                      />
                      <div className="absolute top-0 right-0 p-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveMedia('cover')}
                          className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-md transition-all"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-64 p-6">
                      <div className="bg-blue-100 p-4 rounded-full mb-4">
                        <Image className="h-12 w-12 text-blue-600" />
                      </div>
                      <p className="text-base text-blue-800 mb-3 font-medium">No cover image selected</p>
                      <p className="text-sm text-blue-600 mb-4 text-center max-w-md">
                        Upload a high-quality image to make your post stand out. Recommended size: 1200 x 630 pixels.
                      </p>
                      <button
                        type="button"
                        onClick={() => coverImageInputRef.current?.click()}
                        className="inline-flex items-center px-4 py-5 border border-transparent rounded-md shadow-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Select Cover Image
                      </button>
                      <div className='hidden'>
                        <input
                          ref={coverImageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden text-blue-50"
                          style={{display:'hidden'}}
                          onChange={(e) => {
                            if (e.target.files?.length) {
                              handleFileUpload(e.target.files[0], 'cover');
                            }
                          }}
                        />
                        </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Additional Images</h3>
                <div className="border border-blue-300 rounded-md bg-blue-50 shadow-sm p-6">
                  {postForm.images.length > 0 ? (
                    <div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        {postForm.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`Image ${index + 1}`}
                              className="w-full h-32 object-cover rounded-md shadow-sm border border-gray-300"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <button
                                type="button"
                                onClick={() => handleRemoveMedia('image', index)}
                                className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-md"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        <div className="flex flex-col items-center justify-center h-32 bg-white rounded-md border-2 border-dashed border-blue-300 hover:border-blue-500 transition-colors">
                          <button
                            type="button"
                            onClick={() => imageInputRef.current?.click()}
                            className="flex flex-col items-center justify-center w-full h-full text-blue-600 hover:text-blue-700"
                          >
                            <Plus className="h-8 w-8 mb-1" />
                            <span className="text-sm font-medium">Add Image</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-sm text-blue-700">
                          <span className="font-medium">{postForm.images.length}</span> image{postForm.images.length !== 1 ? 's' : ''} added
                        </p>
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Add more
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-44 p-4">
                      <div className="bg-blue-100 p-3 rounded-full mb-3">
                        <Image className="h-8 w-8 text-blue-600" />
                      </div>
                      <p className="text-base text-blue-800 mb-2 font-medium">No additional images</p>
                      <p className="text-sm text-blue-600 mb-3 text-center max-w-md">
                        Enhance your post with more images. Add product views, diagrams, or supporting visuals.
                      </p>
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="inline-flex items-center px-4 py-5 border border-transparent rounded-md shadow-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Add Images
                      </button>
                    </div>
                  )}
                  <div className='hidden'>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden text-blue-50"
                    style={{display: 'none', display:'hidden'}}
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        handleFileUpload(e.target.files[0], 'image');
                      }
                    }}
                  />
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Supported formats: JPG, PNG, GIF, WEBP. Maximum size: 15MB per image.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Videos</h3>
                <div className="border border-blue-300 rounded-md bg-blue-50 shadow-sm p-6">
                  {postForm.videos.length > 0 ? (
                    <div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        {postForm.videos.map((video, index) => (
                          <div key={index} className="relative group bg-black rounded-md overflow-hidden shadow-sm">
                            <video
                              src={video}
                              controls
                              className="w-full h-32 object-cover rounded-md"
                            />
                            <div className="absolute top-2 right-2">
                              <button
                                type="button"
                                onClick={() => handleRemoveMedia('video', index)}
                                className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-md"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        <div className="flex flex-col items-center justify-center h-32 bg-white rounded-md border-2 border-dashed border-blue-300 hover:border-blue-500 transition-colors">
                          <button
                            type="button"
                            onClick={() => videoInputRef.current?.click()}
                            className="flex flex-col items-center justify-center w-full h-full text-blue-600 hover:text-blue-700"
                          >
                            <Plus className="h-8 w-8 mb-1" />
                            <span className="text-sm font-medium">Add Video</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-sm text-blue-700">
                          <span className="font-medium">{postForm.videos.length}</span> video{postForm.videos.length !== 1 ? 's' : ''} added
                        </p>
                        <button
                          type="button"
                          onClick={() => videoInputRef.current?.click()}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Add more
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-44 p-4">
                      <div className="bg-blue-100 p-3 rounded-full mb-3">
                        <Film className="h-8 w-8 text-blue-600" />
                      </div>
                      <p className="text-base text-blue-800 mb-2 font-medium">No videos added</p>
                      <p className="text-sm text-blue-600 mb-3 text-center max-w-md">
                        Add engaging video content to your post. Product demos, tutorials, or promotional clips.
                      </p>
                      <button
                        type="button"
                        onClick={() => videoInputRef.current?.click()}
                        className="inline-flex items-center px-4 py-5 border border-transparent rounded-md shadow-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Add Video
                      </button>
                    </div>
                  )}
                  <div className="hidden">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      className="hidden text-blue-50"
                      style={{display: 'none', display:'hidden'}}
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          handleFileUpload(e.target.files[0], 'video');
                        }
                      }}
                    />
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Supported formats: MP4, WEBM, AVI, MOV. Maximum size: 50MB per video.
                </p>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Video upload tips</p>
                      <ul className="mt-1 text-xs text-yellow-700 list-disc pl-4 space-y-1">
                        {/* <li>For best performance, compress videos before uploading</li> */}
                        <li>If you encounter errors, try uploading a smaller file</li>
                        <li>Recommended resolution: 1280×720 (720p) or lower for blog posts</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              {/* <div className="flex justify-end">
                <button
                  type="button"
                  onClick={generateSeoFields}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Auto-generate SEO
                </button>
              </div> */}
              {/* SEO Tab AI Button - Replace the existing Auto-generate SEO button */}
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={generateSeoFields}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Auto-generate SEO
                </button>
                <button
                  type="button"
                  onClick={generateAISeo}
                  disabled={aiLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                >
                  {aiLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand className="h-4 w-4 mr-2" />
                      AI SEO Generator
                    </>
                  )}
                </button>
              </div>

              <div>
                <label htmlFor="seoTitle" className="block text-sm font-medium text-gray-700 mb-1">
                  SEO Title
                </label>
                <input
                  type="text"
                  id="seoTitle"
                  name="seoTitle"
                  value={postForm.seoTitle}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="SEO title (shown in search results)"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {postForm.seoTitle.length} / 60 characters recommended
                </p>
              </div>
              
              <div>
                <label htmlFor="seoDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Meta Description
                </label>
                <textarea
                  id="seoDescription"
                  name="seoDescription"
                  value={postForm.seoDescription}
                  onChange={handleInputChange}
                  rows="3"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Meta description (shown in search results)"
                ></textarea>
                <p className="mt-1 text-sm text-gray-500">
                  {postForm.seoDescription.length} / 160 characters recommended
                </p>
              </div>
              
              <div>
                <label htmlFor="seoKeywords" className="block text-sm font-medium text-gray-700 mb-1">
                  Meta Keywords
                </label>
                <input
                  type="text"
                  id="seoKeywords"
                  name="seoKeywords"
                  value={postForm.seoKeywords}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Comma-separated keywords"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Less important for SEO nowadays, but still useful for organization
                </p>
              </div>
              
              <div className="border p-4 rounded-md bg-gray-50">
                <h4 className="font-medium text-sm text-gray-700 mb-2">SEO Preview</h4>
                <div className="border p-3 rounded-md bg-white">
                  <div className="text-blue-600 text-lg truncate">
                    {postForm.seoTitle || postForm.title || 'Post Title'}
                  </div>
                  <div className="text-green-600 text-sm truncate">
                    yourdomain.com/blog/{postForm.title
                      ? postForm.title
                          .toLowerCase()
                          .replace(/[^\w\s]/g, '')
                          .replace(/\s+/g, '-')
                      : 'post-slug'}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">
                    {postForm.seoDescription || 'Your meta description will appear here. It should be concise and engaging to encourage clicks.'}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Form submission buttons at bottom */}
          <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                if (isDirty) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                    navigate('/admin/blog/posts');
                  }
                } else {
                  navigate('/admin/blog/posts');
                }
              }}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {postForm.status === 'published' ? 'Update' : 'Save Draft'}
                </>
              )}
            </button>
            
            {postForm.status !== 'published' && (
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Publishing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Publish
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
      {/* Add the AI Settings Modal */}
      <AiSettingsModal 
        isOpen={showAiSettings}
        onClose={() => setShowAiSettings(false)}
        onApply={handleApplyAiSettings}
        defaultSettings={aiSettings}
      />
              
      <style>{`
        button {
          box-shadow: none !important;
          border: none !important;
          transform: none !important;
        }
        /* Upload area hover effects */
        .upload-area {
          transition: all 0.3s ease-in-out;
        }

        .upload-area:hover {
          background-color: rgba(59, 130, 246, 0.1);
          border-color: rgb(59, 130, 246);
        }

        /* File upload progress bar */
        .upload-progress {
          height: 6px;
          background-color: #e6e6e6;
          border-radius: 3px;
          overflow: hidden;
          margin-top: 8px;
        }

        .upload-progress-bar {
          height: 100%;
          background-color: #4f46e5;
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        /* Custom file input button */
        .custom-file-input {
          color: transparent;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        .custom-file-input::-webkit-file-upload-button {
          visibility: hidden;
        }

        /* Upload animation */
        .upload-icon {
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.7;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Media preview grid */
        .media-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 16px;
        }

        /* Image/video thumbnail */
        .media-thumbnail {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          transition: transform 0.2s;
        }

        .media-thumbnail:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        /* Error state */
        .upload-error {
          border-color: #ef4444;
          background-color: rgba(239, 68, 68, 0.05);
        }

        /* Success overlay for completed uploads */
        .upload-success-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(16, 185, 129, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s;
        }

        .media-thumbnail:hover .upload-success-overlay {
          opacity: 1;
        }
       `}</style>
    </div>
  );
};

export default PostForm;







// // AI but before blue box
// // src/components/admin/blog/PostForm.js
// import React, { useState, useEffect, useRef } from 'react';
// import { useNavigate, useParams } from 'react-router-dom';
// import { Save, Eye, Image, Film, Upload, X, CheckCircle, AlertTriangle,
//   ArrowLeft, Trash2, Plus, Sparkles, RefreshCw, Wand, Settings
// } from 'lucide-react';
// import RichTextEditor from '../shared/RichTextEditor';
// import NotificationBar from '../shared/NotificationBar';
// import MediaUploader from '../shared/MediaUploader';
// import AiSettingsModal from '../shared/AiSettingsModal';

// const PostForm = () => {
//   const [loading, setLoading] = useState(false);
//   const [loadingPost, setLoadingPost] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [tags, setTags] = useState([]);
//   const [notification, setNotification] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [originalPost, setOriginalPost] = useState(null);
//   const [aiLoading, setAiLoading] = useState(false);

//   const [postForm, setPostForm] = useState({
//     title: '',
//     excerpt: '',
//     content: '',
//     coverImage: '',
//     category: '',
//     tags: [],
//     status: 'draft',
//     featured: false,
//     seoTitle: '',
//     seoDescription: '',
//     seoKeywords: '',
//     images: [],
//     videos: [],
//   });
  
//   // For handling content changes from rich text editor
//   const [editorContent, setEditorContent] = useState('');
  
//   // Tabs for organizing the form
//   const [activeTab, setActiveTab] = useState('content');
  
//   // Track if form is dirty (has unsaved changes)
//   const [isDirty, setIsDirty] = useState(false);
  
//   // Refs for file input elements
//   const coverImageInputRef = useRef(null);
//   const imageInputRef = useRef(null);
//   const videoInputRef = useRef(null);
  
//   const navigate = useNavigate();
//   const params = useParams();
//   const postId = params.id;

//   const [showAiSettings, setShowAiSettings] = useState(false);
//   const [aiSettings, setAiSettings] = useState({
//     contentLength: 'medium',
//     tone: 'professional',
//     targetAudience: 'general',
//     customInstructions: '',
//   });
  
//   useEffect(() => {
//     // Fetch categories and tags
//     fetchCategoriesAndTags();
    
//     // If we have a postId, we're editing an existing post
//     if (postId) {
//       setIsEditing(true);
//       fetchPost(postId);
//     }
    
//     // Setup confirmation before leaving page with unsaved changes
//     const handleBeforeUnload = (e) => {
//       if (isDirty) {
//         e.preventDefault();
//         e.returnValue = '';
//       }
//     };
    
//     window.addEventListener('beforeunload', handleBeforeUnload);
    
//     return () => {
//       window.removeEventListener('beforeunload', handleBeforeUnload);
//     };
//   }, [postId]);
  
//   // Update form dirty state when form changes
//   useEffect(() => {
//     if (originalPost) {
//       const hasChanges = JSON.stringify(originalPost) !== JSON.stringify({
//         ...postForm,
//         content: editorContent
//       });
//       setIsDirty(hasChanges);
//     } else {
//       // For new posts, check if any fields have been filled out
//       const isEmpty = !postForm.title && !postForm.excerpt && !editorContent;
//       setIsDirty(!isEmpty);
//     }
//   }, [postForm, editorContent, originalPost]);
  

//   // Function to generate blog content using AI
//   const generateAIContent = async () => {
//     if (!postForm.title) {
//       showNotification('error', 'Please add a title first');
//       return;
//     }
  
//     setAiLoading(true);
//     try {
//       const token = localStorage.getItem('token');
      
//       // Include the AI settings in the request
//       const requestBody = {
//         topic: postForm.title,
//         keywords: postForm.seoKeywords,
//         settings: aiSettings 
//       };
      
//       const res = await fetch('/api/blog-ai/generate-content', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`
//         },
//         body: JSON.stringify(requestBody)
//       });
      
//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || 'Failed to generate content');
//       }
      
//       const data = await res.json();
      
//       // Set the editor content with the AI-generated content
//       setEditorContent(data.content);
//       // Update the form content field
//       setPostForm(prev => ({ ...prev, content: data.content }));
      
//       showNotification('success', 'AI content generated successfully');
//     } catch (error) {
//       console.error('Error generating AI content:', error);
//       let errorMessage = 'Failed to generate content';
//       if (error.response && error.response.data) {
//         errorMessage = error.response.data.details || error.response.data.error || errorMessage;
//       }
      
//       showNotification('error', errorMessage);
//     } finally {
//       setAiLoading(false);
//     }
//   };
  
//   const handleApplyAiSettings = (newSettings) => {
//     setAiSettings(newSettings);
//     showNotification('success', 'AI settings applied');
//   };

//   // Function to generate SEO metadata using AI
//   const generateAISeo = async () => {
//     if (!postForm.title || !editorContent) {
//       showNotification('error', 'Title and content are required for SEO generation');
//       return;
//     }

//     setAiLoading(true);
//     try {
//       const token = localStorage.getItem('token');
//       const res = await fetch('/api/blog-ai/generate-seo', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`
//         },
//         body: JSON.stringify({
//           title: postForm.title,
//           content: editorContent
//         })
//       });
      
//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || 'Failed to generate SEO data');
//       }
      
//       const data = await res.json();
      
//       // Update the form with the AI-generated SEO data
//       setPostForm(prev => ({
//         ...prev,
//         seoTitle: data.seoTitle,
//         seoDescription: data.metaDescription
//       }));
      
//       showNotification('success', 'AI SEO metadata generated successfully');
//     } catch (error) {
//       console.error('Error generating AI SEO data:', error);
//       showNotification('error', error.message || 'Failed to generate SEO data');
//     } finally {
//       setAiLoading(false);
//     }
//   };

//   const validateForm = () => {
//     const errors = [];    
//     if (!postForm.title) {
//       errors.push('Title is required');
//     }    
//     if (!postForm.excerpt) {
//       errors.push('Description is required');
//     }    
//     if (!editorContent) {
//       errors.push('Content is required');
//     }    
//     if (!postForm.category) {
//       errors.push('Category is required');
//     }    
//     return errors;
//   };

//   const fetchCategoriesAndTags = async () => {
//     try {
//       const [categoriesRes, tagsRes] = await Promise.all([
//         fetch('/api/blog/categories'),
//         fetch('/api/blog/tags')
//       ]);
      
//       if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
//       if (!tagsRes.ok) throw new Error('Failed to fetch tags');
      
//       const categoriesData = await categoriesRes.json();
//       const tagsData = await tagsRes.json();
      
//       setCategories(categoriesData);
//       setTags(tagsData);
//     } catch (error) {
//       console.error('Error fetching form data:', error);
//       showNotification('error', 'Failed to load form data');
//     }
//   };
  
//   // const fetchPost = async (id) => {
//   //   setLoadingPost(true);
//   //   try {
//   //     const token = localStorage.getItem('token');
//   //     const res = await fetch(`/api/blog/posts/${id}`, {
//   //       headers: {
//   //         Authorization: `Bearer ${token}`
//   //       }
//   //     });
      
//   //     if (!res.ok) throw new Error('Failed to fetch post');
      
//   //     const post = await res.json();
      
//   //     // Set the form state with the post data
//   //     const formData = {
//   //       title: post.title,
//   //       excerpt: post.excerpt || '',
//   //       content: post.content,
//   //       coverImage: post.coverImage || '',
//   //       category: post.category?._id || '',
//   //       tags: post.tags?.map(tag => tag._id) || [],
//   //       status: post.status || 'draft',
//   //       featured: post.featured || false,
//   //       seoTitle: post.seoTitle || '',
//   //       seoDescription: post.seoDescription || '',
//   //       seoKeywords: post.seoKeywords || '',
//   //       images: post.images || [],
//   //       videos: post.videos || []
//   //     };
      
//   //     setPostForm(formData);
//   //     setEditorContent(post.content);
//   //     setOriginalPost(formData);
//   //   } catch (error) {
//   //     console.error('Error fetching post:', error);
//   //     showNotification('error', 'Failed to load post');
//   //   } finally {
//   //     setLoadingPost(false);
//   //   }
//   // };
 
//   const fetchPost = async (id) => {
//     setLoadingPost(true);
//     try {
//       const token = localStorage.getItem('token');
//       // Change the URL to use the new route with /id/ prefix
//       const res = await fetch(`/api/blog/posts/id/${id}`, {
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });
      
//       if (!res.ok) throw new Error('Failed to fetch post');
      
//       const post = await res.json();
      
//       // Set the form state with the post data
//       const formData = {
//         title: post.title,
//         excerpt: post.excerpt || '',
//         content: post.content,
//         coverImage: post.coverImage || '',
//         category: post.category?._id || '',
//         tags: post.tags?.map(tag => tag._id) || [],
//         status: post.status || 'draft',
//         featured: post.featured || false,
//         seoTitle: post.seoTitle || '',
//         seoDescription: post.seoDescription || '',
//         seoKeywords: post.seoKeywords || '',
//         images: post.images || [],
//         videos: post.videos || []
//       };
      
//       setPostForm(formData);
//       setEditorContent(post.content);
//       setOriginalPost(formData);
//     } catch (error) {
//       console.error('Error fetching post:', error);
//       showNotification('error', 'Failed to load post');
//     } finally {
//       setLoadingPost(false);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setPostForm(prev => ({ ...prev, [name]: value }));
//   };
  
//   const handleTagsChange = (e) => {
//     const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
//     setPostForm(prev => ({ ...prev, tags: selectedOptions }));
//   };
  
//   const handleEditorChange = (content) => {
//     setEditorContent(content);
//     // Also update the postForm content field to keep it in sync
//     setPostForm(prev => ({ ...prev, content }));
//   };
  
//   // const handleSubmit = async (e, publishAfterSave = false) => {
//   //   e.preventDefault();
//   //   setLoading(true);
    
//   //   try {
//   //     const token = localStorage.getItem('token');
//   //     const method = isEditing ? 'PUT' : 'POST';
//   //     const url = isEditing 
//   //       ? `/api/blog/posts/${postId}`
//   //       : '/api/blog/posts';
      
//   //     // If publishing, override the status
//   //     const formData = {
//   //       ...postForm,
//   //       content: editorContent,
//   //       status: publishAfterSave ? 'published' : postForm.status
//   //     };
      
//   //     const res = await fetch(url, {
//   //       method,
//   //       headers: {
//   //         'Content-Type': 'application/json',
//   //         Authorization: `Bearer ${token}`
//   //       },
//   //       body: JSON.stringify(formData)
//   //     });
      
//   //     if (!res.ok) {
//   //       const errorData = await res.json();
//   //       throw new Error(errorData.error || 'Failed to save post');
//   //     }
      
//   //     const data = await res.json();
      
//   //     // Update original post to reset dirty state
//   //     setOriginalPost(formData);
//   //     setIsDirty(false);
      
//   //     showNotification('success', `Post ${isEditing ? 'updated' : 'created'} successfully`);
      
//   //     // Redirect to posts list after short delay
//   //     setTimeout(() => {
//   //       navigate('/admin/blog/posts');
//   //     }, 1500);
//   //   } catch (error) {
//   //     console.error('Error saving post:', error);
//   //     showNotification('error', error.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };
//   // Replace or modify the handleSubmit function in PostForm.js
//   // In handleSubmit function in PostForm.js
//   const handleSubmit = async (e, publishAfterSave = false) => {
//     e.preventDefault();
    
//     // Validate form before submission
//     const validationErrors = validateForm();
//     if (validationErrors.length > 0) {
//       showNotification('error', `Please fix the following errors: ${validationErrors.join(', ')}`);
//       return;
//     }
    
//     setLoading(true);
    
//     try {
//       const token = localStorage.getItem('token');
//       const method = isEditing ? 'PUT' : 'POST';
//       const url = isEditing 
//         ? `/api/blog/posts/${postId}`
//         : '/api/blog/posts';
      
//       // If publishing, override the status
//       const formData = {
//         ...postForm,
//         content: editorContent,
//         status: publishAfterSave ? 'published' : postForm.status
//       };

//       console.log('Submitting form data:', JSON.stringify(formData)); // Better debugging
      
//       const res = await fetch(url, {
//         method,
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`
//         },
//         body: JSON.stringify(formData)
//       });
      
//       if (!res.ok) {
//         const errorText = await res.text();
//         console.error('Server response:', errorText);
//         const errorData = JSON.parse(errorText);
//         throw new Error(errorData.error || 'Failed to save post');
//       }
      
//       const data = await res.json();
      
//       // Update original post to reset dirty state
//       setOriginalPost(formData);
//       setIsDirty(false);
      
//       showNotification('success', `Post ${isEditing ? 'updated' : 'created'} successfully`);
      
//       // Redirect to posts list after short delay
//       setTimeout(() => {
//         navigate('/admin/blog/posts');
//       }, 1500);
//     } catch (error) {
//       console.error('Error saving post:', error);
//       showNotification('error', error.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
//     } finally {
//       setLoading(false);
//     }
//   };
    

//   const handleFileUpload = async (file, type) => {
//     if (!file) return;
    
//     // Check file size before uploading
//     const maxSizeInBytes = 95 * 1024 * 1024; // 95MB (slightly under server limit)
//     if (file.size > maxSizeInBytes) {
//       showNotification('error', `File size exceeds limit. Maximum size is 95MB.`);
//       return null;
//     }
    
//     // Create formData
//     const formData = new FormData();
//     formData.append('file', file);
    
//     try {
//       const token = localStorage.getItem('token');
      
//       // Display progress notification
//       showNotification('info', `Uploading ${type === 'video' ? 'video' : 'image'}...`, 0); // 0 means don't auto-close
      
//       // Show upload progress in notification
//       const controller = new AbortController();
//       const timeoutId = setTimeout(() => controller.abort(), 120000); // 2-minute timeout
      
//       const res = await fetch('/api/blog/upload', {
//         method: 'POST',
//         headers: {
//           Authorization: `Bearer ${token}`
//         },
//         body: formData,
//         signal: controller.signal
//       });
      
//       clearTimeout(timeoutId);
      
//       // Clear the progress notification
//       setNotification(null);
      
//       if (!res.ok) {
//         // Try to parse error as JSON
//         let errorData;
//         const contentType = res.headers.get('content-type');
        
//         if (contentType && contentType.includes('application/json')) {
//           errorData = await res.json();
//         } else {
//           // Handle non-JSON response
//           const text = await res.text();
//           throw new Error(`Server returned status ${res.status}: ${text.substring(0, 100)}...`);
//         }
        
//         throw new Error(errorData.error || 'Failed to upload file');
//       }
      
//       const data = await res.json();
      
//       // Update form based on file type
//       if (type === 'cover') {
//         setPostForm(prev => ({
//           ...prev,
//           coverImage: data.filePath
//         }));
//       } else if (type === 'image') {
//         setPostForm(prev => ({
//           ...prev,
//           images: [...prev.images, data.filePath]
//         }));
//       } else if (type === 'video') {
//         setPostForm(prev => ({
//           ...prev,
//           videos: [...prev.videos, data.filePath]
//         }));
//       }
      
//       showNotification('success', `${type === 'video' ? 'Video' : 'Image'} uploaded successfully`);
//       return data.filePath;
//     } catch (error) {
//       console.error('Error uploading file:', error);
//       showNotification('error', error.message || 'Failed to upload file. Please try again with a smaller file.');
//       return null;
//     }
//   };
  
//   // Helper function to format file size for display
//   const formatFileSize = (bytes) => {
//     if (bytes === 0) return '0 Bytes';
//     const k = 1024;
//     const sizes = ['Bytes', 'KB', 'MB', 'GB'];
//     const i = Math.floor(Math.log(bytes) / Math.log(k));
//     return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
//   };
  
//   const handleRemoveMedia = (type, index) => {
//     if (type === 'cover') {
//       setPostForm(prev => ({
//         ...prev,
//         coverImage: ''
//       }));
//     } else if (type === 'image') {
//       setPostForm(prev => ({
//         ...prev,
//         images: prev.images.filter((_, i) => i !== index)
//       }));
//     } else if (type === 'video') {
//       setPostForm(prev => ({
//         ...prev,
//         videos: prev.videos.filter((_, i) => i !== index)
//       }));
//     }
//   };
  
//   const showNotification = (type, message, duration = 5000) => {
//     setNotification({ type, message });
//     if (duration) {
//       setTimeout(() => setNotification(null), duration);
//     }
//   };
  
//   // Generate SEO fields from content
//   const generateSeoFields = () => {
//     // If title is empty, don't try to generate SEO fields
//     if (!postForm.title) {
//       showNotification('error', 'Please add a title first');
//       return;
//     }
    
//     // Generate SEO title if empty
//     if (!postForm.seoTitle) {
//       setPostForm(prev => ({
//         ...prev,
//         seoTitle: prev.title
//       }));
//     }
    
//     // Generate SEO description from excerpt or content
//     if (!postForm.seoDescription) {
//       let description = '';
      
//       if (postForm.excerpt) {
//         description = postForm.excerpt;
//       } else if (editorContent) {
//         // Strip HTML tags and get first 160 characters
//         const plainText = editorContent.replace(/<[^>]*>?/gm, '');
//         description = plainText.substring(0, 160).trim();
        
//         // Add ellipsis if truncated
//         if (plainText.length > 160) {
//           description += '...';
//         }
//       }
      
//       setPostForm(prev => ({
//         ...prev,
//         seoDescription: description
//       }));
//     }
    
//     // Generate keywords from title and content
//     if (!postForm.seoKeywords) {
//       // Extract key terms from title and content
//       const allText = `${postForm.title} ${editorContent.replace(/<[^>]*>?/gm, '')}`;
      
//       // Split into words, filter out short words and common stop words
//       const stopWords = ['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'of'];
//       const words = allText.toLowerCase()
//         .replace(/[^\w\s]/g, '')
//         .split(/\s+/)
//         .filter(word => word.length > 3 && !stopWords.includes(word));
      
//       // Count frequency
//       const wordCounts = {};
//       words.forEach(word => {
//         wordCounts[word] = (wordCounts[word] || 0) + 1;
//       });
      
//       // Get top 5-8 most frequent words
//       const keywords = Object.entries(wordCounts)
//         .sort((a, b) => b[1] - a[1])
//         .slice(0, 8)
//         .map(entry => entry[0])
//         .join(', ');
      
//       setPostForm(prev => ({
//         ...prev,
//         seoKeywords: keywords
//       }));
//     }
    
//     showNotification('success', 'SEO fields generated');
//   };
  
//   if (loadingPost) {
//     return (
//       <div className="bg-white rounded-lg shadow-md p-6">
//         <div className="flex justify-center items-center h-64">
//           <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
//           <p className="ml-2 text-gray-500">Loading post...</p>
//         </div>
//       </div>
//     );
//   }
  
//   return (
//     <div className="bg-white pt-4 rounded-lg shadow-md">
//       {notification && (
//         <NotificationBar
//           type={notification.type}
//           message={notification.message}
//           onClose={() => setNotification(null)}
//         />
//       )}
      
//       <div className="px-1 pr-6 py-4 border-b border-gray-200">
//         <div className="flex justify-between items-center">
//           <div className="flex items-center">
//             <button
//               onClick={() => {
//                 if (isDirty) {
//                   if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
//                     navigate('/admin/blog/posts');
//                   }
//                 } else {
//                   navigate('/admin/blog/posts');
//                 }
//               }}
//               className="text-gray-500 hover:text-gray-700"
//             >
//               <ArrowLeft className="h-5 w-5" />
//             </button>
//             <h2 className="text-xl font-semibold">
//               {isEditing ? 'Edit Post' : 'Create New Post'}
//             </h2>
//           </div>
          
//           <div className="flex items-center">
//             <span className="mr-2 text-sm text-gray-500">Status:</span>
//             <select
//               name="status"
//               value={postForm.status}
//               onChange={handleInputChange}
//               className="border border-gray-300 rounded-md text-sm py-1 px-2 focus:ring-blue-500 focus:border-blue-500"
//             >
//               <option value="draft">Draft</option>
//               <option value="published">Published</option>
//             </select>
//           </div>
//         </div>
        
//         <div className="flex justify-end mt-3">
//           <div className="flex items-center space-x-2">
//             <button
//               onClick={(e) => handleSubmit(e, false)}
//               disabled={loading}
//               className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
//             >
//               <Save className="h-4 w-4 mr-2" />
//               Save
//             </button>
            
//             <button
//               onClick={(e) => handleSubmit(e, true)}
//               disabled={loading}
//               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
//             >
//               <CheckCircle className="h-4 w-4 mr-2" />
//               {postForm.status === 'published' ? 'Update' : 'Publish'}
//             </button>
            
//             <button
//               onClick={() => window.open(`/blog/${originalPost?.slug}`, '_blank')}
//               disabled={!isEditing || postForm.status !== 'published'}
//               className={`px-4 py-2 rounded-md flex items-center ${
//                 isEditing && postForm.status === 'published'
//                   ? 'bg-green-600 text-white hover:bg-green-700'
//                   : 'bg-gray-100 text-gray-400 cursor-not-allowed'
//               }`}
//             >
//               <Eye className="h-4 w-4 mr-2" />
//               View
//             </button>
//           </div>
//         </div>
//       </div>
      
//       {/* Post Form */}
//       <div className="p-6">
//         <form onSubmit={(e) => handleSubmit(e, false)}>
//           {/* Tabs */}
//           <div className="border-b border-gray-200 mb-6">
//             <div className="flex -mb-px space-x-8">
//               <button
//                 type="button"
//                 onClick={() => setActiveTab('content')}
//                 className={`pb-4 px-1 border-b-2 font-medium text-sm ${
//                   activeTab === 'content'
//                     ? 'border-blue-500 text-blue-600'
//                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//                 }`}
//               >
//                 Content
//               </button>
//               <button
//                 type="button"
//                 onClick={() => setActiveTab('media')}
//                 className={`pb-4 px-1 border-b-2 font-medium text-sm ${
//                   activeTab === 'media'
//                     ? 'border-blue-500 text-blue-600'
//                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//                 }`}
//               >
//                 Media
//               </button>
//               <button
//                 type="button"
//                 onClick={() => setActiveTab('seo')}
//                 className={`pb-4 px-1 border-b-2 font-medium text-sm ${
//                   activeTab === 'seo'
//                     ? 'border-blue-500 text-blue-600'
//                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//                 }`}
//               >
//                 SEO
//               </button>
//             </div>
//           </div>
          
//           {/* Content Tab */}
//           {activeTab === 'content' && (
//             <div className="space-y-6">
//               <div>
//                 <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
//                   Title <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   id="title"
//                   name="title"
//                   required
//                   value={postForm.title}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Post title"
//                 />
//                   <p className="mt-1 text-sm text-gray-500">
//                   {postForm.title.length} / 55 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
//                  Description
//                 </label>
//                 <textarea
//                   id="excerpt"
//                   name="excerpt"
//                   value={postForm.excerpt}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Brief description (shown in previews)"
//                 ></textarea>
//                 <p className="mt-1 text-sm text-gray-500">
//                   {postForm.excerpt.length} / 160 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
//                   Category <span className="text-red-500">*</span>
//                 </label>
//                 <select
//                   id="category"
//                   name="category"
//                   required
//                   value={postForm.category}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                 >
//                   <option value="">Select a category</option>
//                   {categories.map(category => (
//                     <option key={category._id} value={category._id}>
//                       {category.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>
              
//               <div>
//                 <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
//                   Tags
//                 </label>
//                 <select
//                   id="tags"
//                   name="tags"
//                   multiple
//                   value={postForm.tags}
//                   onChange={handleTagsChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 h-32"
//                 >
//                   {tags.map(tag => (
//                     <option key={tag._id} value={tag._id}>
//                       {tag.name}
//                     </option>
//                   ))}
//                 </select>
//                 <p className="mt-1 text-sm text-gray-500">
//                   Hold Ctrl (or Command on Mac) to select multiple tags
//                 </p>
//               </div>

//               <div className="flex items-center mt-2">
//                 <input
//                   type="checkbox"
//                   id="featured"
//                   name="featured"
//                   checked={postForm.featured}
//                   onChange={(e) => setPostForm(prev => ({ ...prev, featured: e.target.checked }))}
//                   className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                 />
//                 <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
//                   Feature this post
//                 </label>
//               </div>
              

//               {/* AI Content Generation Button - Add this right above the RichTextEditor component */}
//               <div className="flex justify-end mb-2 space-x-2">
//                 <button
//                   type="button"
//                   onClick={() => setShowAiSettings(true)}
//                   className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
//                 >
//                   <Settings className="h-4 w-4 mr-2" />
//                   AI Settings
//                 </button>
                
//                 <button
//                   type="button"
//                   onClick={generateAIContent}
//                   disabled={aiLoading}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
//                 >
//                   {aiLoading ? (
//                     <>
//                       <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
//                       Generating...
//                     </>
//                   ) : (
//                     <>
//                       <Sparkles className="h-4 w-4 mr-2" />
//                       Generate AI Content
//                     </>
//                   )}
//                 </button>
//               </div>

              

//               <div>
//                 <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
//                   Content <span className="text-red-500">*</span>
//                 </label>
//                 <RichTextEditor 
//                   initialContent={editorContent}
//                   onChange={handleEditorChange}
//                   placeholder="Write your post content here..."
//                 />
//               </div>
//             </div>
//           )}
          
//           {/* Media Tab */}
//           {activeTab === 'media' && (
//             <div className="space-y-6">
//               <div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-3">Cover Image</h3>
//                 <div className="border border-gray-300 rounded-md p-4">
//                   {postForm.coverImage ? (
//                     <div className="relative">
//                       <img
//                         src={postForm.coverImage}
//                         alt="Cover"
//                         className="w-full h-64 object-cover rounded-md"
//                       />
//                       <button
//                         type="button"
//                         onClick={() => handleRemoveMedia('cover')}
//                         className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
//                       >
//                         <X className="h-4 w-4" />
//                       </button>
//                     </div>
//                   ) : (
//                     <div className="flex flex-col items-center justify-center h-64 bg-gray-100 rounded-md">
//                       <Image className="h-16 w-16 text-gray-400 mb-2" />
//                       <p className="text-sm text-gray-500 mb-2">No cover image selected</p>
//                       <button
//                         type="button"
//                         onClick={() => coverImageInputRef.current?.click()}
//                         className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                       >
//                         <Upload className="h-4 w-4 mr-2" />
//                         Select Cover Image
//                       </button>
//                       <input
//                         ref={coverImageInputRef}
//                         type="file"
//                         accept="image/*"
//                         className="hidden"
//                         onChange={(e) => {
//                           if (e.target.files?.length) {
//                             handleFileUpload(e.target.files[0], 'cover');
//                           }
//                         }}
//                       />
//                     </div>
//                   )}
//                 </div>
//               </div>
              
//               <div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-3">Additional Images</h3>
//                 <div className="border border-gray-300 rounded-md p-4">
//                   {postForm.images.length > 0 ? (
//                     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
//                       {postForm.images.map((image, index) => (
//                         <div key={index} className="relative">
//                           <img
//                             src={image}
//                             alt={`Image ${index + 1}`}
//                             className="w-full h-32 object-cover rounded-md"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => handleRemoveMedia('image', index)}
//                             className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
//                           >
//                             <X className="h-4 w-4" />
//                           </button>
//                         </div>
//                       ))}
//                       <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                         <button
//                           type="button"
//                           onClick={() => imageInputRef.current?.click()}
//                           className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                         >
//                           <Plus className="h-4 w-4 mr-1" />
//                           Add Image
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                       <Image className="h-8 w-8 text-gray-400 mb-2" />
//                       <p className="text-sm text-gray-500 mb-2">No additional images</p>
//                       <button
//                         type="button"
//                         onClick={() => imageInputRef.current?.click()}
//                         className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                       >
//                         <Upload className="h-4 w-4 mr-1" />
//                         Add Images
//                       </button>
//                     </div>
//                   )}
//                   <input
//                     ref={imageInputRef}
//                     type="file"
//                     accept="image/*"
//                     className="hidden"
//                     onChange={(e) => {
//                       if (e.target.files?.length) {
//                         handleFileUpload(e.target.files[0], 'image');
//                       }
//                     }}
//                   />
//                 </div>
//               </div>
              
//               <div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-3">Videos</h3>
//                 <div className="border border-gray-300 rounded-md p-4">
//                   {postForm.videos.length > 0 ? (
//                     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
//                       {postForm.videos.map((video, index) => (
//                         <div key={index} className="relative">
//                           <video
//                             src={video}
//                             controls
//                             className="w-full h-32 rounded-md bg-black"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => handleRemoveMedia('video', index)}
//                             className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
//                           >
//                             <X className="h-4 w-4" />
//                           </button>
//                         </div>
//                       ))}
//                       <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                         <button
//                           type="button"
//                           onClick={() => videoInputRef.current?.click()}
//                           className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                         >
//                           <Plus className="h-4 w-4 mr-1" />
//                           Add Video
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                       <Film className="h-8 w-8 text-gray-400 mb-2" />
//                       <p className="text-sm text-gray-500 mb-2">No videos added</p>
//                       <button
//                         type="button"
//                         onClick={() => videoInputRef.current?.click()}
//                         className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                       >
//                         <Upload className="h-4 w-4 mr-1" />
//                         Add Video
//                       </button>
//                     </div>
//                   )}
//                   <input
//                     ref={videoInputRef}
//                     type="file"
//                     accept="video/*"
//                     className="hidden"
//                     onChange={(e) => {
//                       if (e.target.files?.length) {
//                         handleFileUpload(e.target.files[0], 'video');
//                       }
//                     }}
//                   />
//                 </div>
//               </div>
//             </div>
//           )}
          
//           {/* SEO Tab */}
//           {activeTab === 'seo' && (
//             <div className="space-y-6">
//               {/* <div className="flex justify-end">
//                 <button
//                   type="button"
//                   onClick={generateSeoFields}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
//                 >
//                   <CheckCircle className="h-4 w-4 mr-2" />
//                   Auto-generate SEO
//                 </button>
//               </div> */}
//               {/* SEO Tab AI Button - Replace the existing Auto-generate SEO button */}
//               <div className="flex justify-end space-x-2">
//                 <button
//                   type="button"
//                   onClick={generateSeoFields}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
//                 >
//                   <CheckCircle className="h-4 w-4 mr-2" />
//                   Auto-generate SEO
//                 </button>
//                 <button
//                   type="button"
//                   onClick={generateAISeo}
//                   disabled={aiLoading}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
//                 >
//                   {aiLoading ? (
//                     <>
//                       <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
//                       Generating...
//                     </>
//                   ) : (
//                     <>
//                       <Wand className="h-4 w-4 mr-2" />
//                       AI SEO Generator
//                     </>
//                   )}
//                 </button>
//               </div>

//               <div>
//                 <label htmlFor="seoTitle" className="block text-sm font-medium text-gray-700 mb-1">
//                   SEO Title
//                 </label>
//                 <input
//                   type="text"
//                   id="seoTitle"
//                   name="seoTitle"
//                   value={postForm.seoTitle}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="SEO title (shown in search results)"
//                 />
//                 <p className="mt-1 text-sm text-gray-500">
//                   {postForm.seoTitle.length} / 60 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="seoDescription" className="block text-sm font-medium text-gray-700 mb-1">
//                   Meta Description
//                 </label>
//                 <textarea
//                   id="seoDescription"
//                   name="seoDescription"
//                   value={postForm.seoDescription}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Meta description (shown in search results)"
//                 ></textarea>
//                 <p className="mt-1 text-sm text-gray-500">
//                   {postForm.seoDescription.length} / 160 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="seoKeywords" className="block text-sm font-medium text-gray-700 mb-1">
//                   Meta Keywords
//                 </label>
//                 <input
//                   type="text"
//                   id="seoKeywords"
//                   name="seoKeywords"
//                   value={postForm.seoKeywords}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Comma-separated keywords"
//                 />
//                 <p className="mt-1 text-sm text-gray-500">
//                   Less important for SEO nowadays, but still useful for organization
//                 </p>
//               </div>
              
//               <div className="border p-4 rounded-md bg-gray-50">
//                 <h4 className="font-medium text-sm text-gray-700 mb-2">SEO Preview</h4>
//                 <div className="border p-3 rounded-md bg-white">
//                   <div className="text-blue-600 text-lg truncate">
//                     {postForm.seoTitle || postForm.title || 'Post Title'}
//                   </div>
//                   <div className="text-green-600 text-sm truncate">
//                     yourdomain.com/blog/{postForm.title
//                       ? postForm.title
//                           .toLowerCase()
//                           .replace(/[^\w\s]/g, '')
//                           .replace(/\s+/g, '-')
//                       : 'post-slug'}
//                   </div>
//                   <div className="text-gray-600 text-sm mt-1">
//                     {postForm.seoDescription || 'Your meta description will appear here. It should be concise and engaging to encourage clicks.'}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
          
//           {/* Form submission buttons at bottom */}
//           <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3">
//             <button
//               type="button"
//               onClick={() => {
//                 if (isDirty) {
//                   if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
//                     navigate('/admin/blog/posts');
//                   }
//                 } else {
//                   navigate('/admin/blog/posts');
//                 }
//               }}
//               className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
//             >
//               Cancel
//             </button>
            
//             <button
//               type="submit"
//               disabled={loading}
//               className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center"
//             >
//               {loading ? (
//                 <>
//                   <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
//                   Saving...
//                 </>
//               ) : (
//                 <>
//                   <Save className="h-4 w-4 mr-2" />
//                   {postForm.status === 'published' ? 'Update' : 'Save Draft'}
//                 </>
//               )}
//             </button>
            
//             {postForm.status !== 'published' && (
//               <button
//                 type="button"
//                 onClick={(e) => handleSubmit(e, true)}
//                 disabled={loading}
//                 className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center"
//               >
//                 {loading ? (
//                   <>
//                     <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
//                     Publishing...
//                   </>
//                 ) : (
//                   <>
//                     <CheckCircle className="h-4 w-4 mr-2" />
//                     Publish
//                   </>
//                 )}
//               </button>
//             )}
//           </div>
//         </form>
//       </div>
//       {/* Add the AI Settings Modal */}
//       <AiSettingsModal 
//                 isOpen={showAiSettings}
//                 onClose={() => setShowAiSettings(false)}
//                 onApply={handleApplyAiSettings}
//                 defaultSettings={aiSettings}
//               />
              
//       <style jsx="true">{`
//         button {
//           box-shadow: none !important;
//           border: none !important;
//           transform: none !important;
//         }
//        `}</style>
//     </div>
//   );
// };

// export default PostForm;























// before AI

// // src/components/admin/blog/PostForm.js
// import React, { useState, useEffect, useRef } from 'react';
// import { useNavigate, useParams } from 'react-router-dom';
// import { Save, Eye, Image, Film, Upload, X, CheckCircle, AlertTriangle,
//   ArrowLeft, Trash2, Plus, Sparkles, RefreshCw, Wand
// } from 'lucide-react';
// import RichTextEditor from '../shared/RichTextEditor';
// import NotificationBar from '../shared/NotificationBar';
// import MediaUploader from '../shared/MediaUploader';

// const PostForm = () => {
//   const [loading, setLoading] = useState(false);
//   const [loadingPost, setLoadingPost] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [tags, setTags] = useState([]);
//   const [notification, setNotification] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [originalPost, setOriginalPost] = useState(null);
//   const [aiLoading, setAiLoading] = useState(false);

//   const [postForm, setPostForm] = useState({
//     title: '',
//     excerpt: '',
//     content: '',
//     coverImage: '',
//     category: '',
//     tags: [],
//     status: 'draft',
//     featured: false,
//     seoTitle: '',
//     seoDescription: '',
//     seoKeywords: '',
//     images: [],
//     videos: [],
//   });
  
//   // For handling content changes from rich text editor
//   const [editorContent, setEditorContent] = useState('');
  
//   // Tabs for organizing the form
//   const [activeTab, setActiveTab] = useState('content');
  
//   // Track if form is dirty (has unsaved changes)
//   const [isDirty, setIsDirty] = useState(false);
  
//   // Refs for file input elements
//   const coverImageInputRef = useRef(null);
//   const imageInputRef = useRef(null);
//   const videoInputRef = useRef(null);
  
//   const navigate = useNavigate();
//   const params = useParams();
//   const postId = params.id;
  
//   useEffect(() => {
//     // Fetch categories and tags
//     fetchCategoriesAndTags();
    
//     // If we have a postId, we're editing an existing post
//     if (postId) {
//       setIsEditing(true);
//       fetchPost(postId);
//     }
    
//     // Setup confirmation before leaving page with unsaved changes
//     const handleBeforeUnload = (e) => {
//       if (isDirty) {
//         e.preventDefault();
//         e.returnValue = '';
//       }
//     };
    
//     window.addEventListener('beforeunload', handleBeforeUnload);
    
//     return () => {
//       window.removeEventListener('beforeunload', handleBeforeUnload);
//     };
//   }, [postId]);
  
//   // Update form dirty state when form changes
//   useEffect(() => {
//     if (originalPost) {
//       const hasChanges = JSON.stringify(originalPost) !== JSON.stringify({
//         ...postForm,
//         content: editorContent
//       });
//       setIsDirty(hasChanges);
//     } else {
//       // For new posts, check if any fields have been filled out
//       const isEmpty = !postForm.title && !postForm.excerpt && !editorContent;
//       setIsDirty(!isEmpty);
//     }
//   }, [postForm, editorContent, originalPost]);
  

//   // Function to generate blog content using AI
//   const generateAIContent = async () => {
//     if (!postForm.title) {
//       showNotification('error', 'Please add a title first');
//       return;
//     }

//     setAiLoading(true);
//     try {
//       const token = localStorage.getItem('token');
//       const res = await fetch('/api/blog-ai/generate-content', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`
//         },
//         body: JSON.stringify({
//           topic: postForm.title,
//           keywords: postForm.seoKeywords
//         })
//       });
      
//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || 'Failed to generate content');
//       }
      
//       const data = await res.json();
      
//       // Set the editor content with the AI-generated content
//       setEditorContent(data.content);
//       // Update the form content field
//       setPostForm(prev => ({ ...prev, content: data.content }));
      
//       showNotification('success', 'AI content generated successfully');
//     } catch (error) {
//       console.error('Error generating AI content:', error);
//       let errorMessage = 'Failed to generate content';
//       if (error.response && error.response.data) {
//         errorMessage = error.response.data.details || error.response.data.error || errorMessage;
//       }
      
//       showNotification('error', errorMessage);

//     } finally {
//       setAiLoading(false);
//     }
//   };

//   // Function to generate SEO metadata using AI
//   const generateAISeo = async () => {
//     if (!postForm.title || !editorContent) {
//       showNotification('error', 'Title and content are required for SEO generation');
//       return;
//     }

//     setAiLoading(true);
//     try {
//       const token = localStorage.getItem('token');
//       const res = await fetch('/api/blog-ai/generate-seo', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`
//         },
//         body: JSON.stringify({
//           title: postForm.title,
//           content: editorContent
//         })
//       });
      
//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || 'Failed to generate SEO data');
//       }
      
//       const data = await res.json();
      
//       // Update the form with the AI-generated SEO data
//       setPostForm(prev => ({
//         ...prev,
//         seoTitle: data.seoTitle,
//         seoDescription: data.metaDescription
//       }));
      
//       showNotification('success', 'AI SEO metadata generated successfully');
//     } catch (error) {
//       console.error('Error generating AI SEO data:', error);
//       showNotification('error', error.message || 'Failed to generate SEO data');
//     } finally {
//       setAiLoading(false);
//     }
//   };

//   const validateForm = () => {
//     const errors = [];    
//     if (!postForm.title) {
//       errors.push('Title is required');
//     }    
//     if (!postForm.excerpt) {
//       errors.push('Description is required');
//     }    
//     if (!editorContent) {
//       errors.push('Content is required');
//     }    
//     if (!postForm.category) {
//       errors.push('Category is required');
//     }    
//     return errors;
//   };

//   const fetchCategoriesAndTags = async () => {
//     try {
//       const [categoriesRes, tagsRes] = await Promise.all([
//         fetch('/api/blog/categories'),
//         fetch('/api/blog/tags')
//       ]);
      
//       if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
//       if (!tagsRes.ok) throw new Error('Failed to fetch tags');
      
//       const categoriesData = await categoriesRes.json();
//       const tagsData = await tagsRes.json();
      
//       setCategories(categoriesData);
//       setTags(tagsData);
//     } catch (error) {
//       console.error('Error fetching form data:', error);
//       showNotification('error', 'Failed to load form data');
//     }
//   };
  
//   // const fetchPost = async (id) => {
//   //   setLoadingPost(true);
//   //   try {
//   //     const token = localStorage.getItem('token');
//   //     const res = await fetch(`/api/blog/posts/${id}`, {
//   //       headers: {
//   //         Authorization: `Bearer ${token}`
//   //       }
//   //     });
      
//   //     if (!res.ok) throw new Error('Failed to fetch post');
      
//   //     const post = await res.json();
      
//   //     // Set the form state with the post data
//   //     const formData = {
//   //       title: post.title,
//   //       excerpt: post.excerpt || '',
//   //       content: post.content,
//   //       coverImage: post.coverImage || '',
//   //       category: post.category?._id || '',
//   //       tags: post.tags?.map(tag => tag._id) || [],
//   //       status: post.status || 'draft',
//   //       featured: post.featured || false,
//   //       seoTitle: post.seoTitle || '',
//   //       seoDescription: post.seoDescription || '',
//   //       seoKeywords: post.seoKeywords || '',
//   //       images: post.images || [],
//   //       videos: post.videos || []
//   //     };
      
//   //     setPostForm(formData);
//   //     setEditorContent(post.content);
//   //     setOriginalPost(formData);
//   //   } catch (error) {
//   //     console.error('Error fetching post:', error);
//   //     showNotification('error', 'Failed to load post');
//   //   } finally {
//   //     setLoadingPost(false);
//   //   }
//   // };
 
//   const fetchPost = async (id) => {
//     setLoadingPost(true);
//     try {
//       const token = localStorage.getItem('token');
//       // Change the URL to use the new route with /id/ prefix
//       const res = await fetch(`/api/blog/posts/id/${id}`, {
//         headers: {
//           Authorization: `Bearer ${token}`
//         }
//       });
      
//       if (!res.ok) throw new Error('Failed to fetch post');
      
//       const post = await res.json();
      
//       // Set the form state with the post data
//       const formData = {
//         title: post.title,
//         excerpt: post.excerpt || '',
//         content: post.content,
//         coverImage: post.coverImage || '',
//         category: post.category?._id || '',
//         tags: post.tags?.map(tag => tag._id) || [],
//         status: post.status || 'draft',
//         featured: post.featured || false,
//         seoTitle: post.seoTitle || '',
//         seoDescription: post.seoDescription || '',
//         seoKeywords: post.seoKeywords || '',
//         images: post.images || [],
//         videos: post.videos || []
//       };
      
//       setPostForm(formData);
//       setEditorContent(post.content);
//       setOriginalPost(formData);
//     } catch (error) {
//       console.error('Error fetching post:', error);
//       showNotification('error', 'Failed to load post');
//     } finally {
//       setLoadingPost(false);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setPostForm(prev => ({ ...prev, [name]: value }));
//   };
  
//   const handleTagsChange = (e) => {
//     const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
//     setPostForm(prev => ({ ...prev, tags: selectedOptions }));
//   };
  
//   const handleEditorChange = (content) => {
//     setEditorContent(content);
//     // Also update the postForm content field to keep it in sync
//     setPostForm(prev => ({ ...prev, content }));
//   };
  
//   // const handleSubmit = async (e, publishAfterSave = false) => {
//   //   e.preventDefault();
//   //   setLoading(true);
    
//   //   try {
//   //     const token = localStorage.getItem('token');
//   //     const method = isEditing ? 'PUT' : 'POST';
//   //     const url = isEditing 
//   //       ? `/api/blog/posts/${postId}`
//   //       : '/api/blog/posts';
      
//   //     // If publishing, override the status
//   //     const formData = {
//   //       ...postForm,
//   //       content: editorContent,
//   //       status: publishAfterSave ? 'published' : postForm.status
//   //     };
      
//   //     const res = await fetch(url, {
//   //       method,
//   //       headers: {
//   //         'Content-Type': 'application/json',
//   //         Authorization: `Bearer ${token}`
//   //       },
//   //       body: JSON.stringify(formData)
//   //     });
      
//   //     if (!res.ok) {
//   //       const errorData = await res.json();
//   //       throw new Error(errorData.error || 'Failed to save post');
//   //     }
      
//   //     const data = await res.json();
      
//   //     // Update original post to reset dirty state
//   //     setOriginalPost(formData);
//   //     setIsDirty(false);
      
//   //     showNotification('success', `Post ${isEditing ? 'updated' : 'created'} successfully`);
      
//   //     // Redirect to posts list after short delay
//   //     setTimeout(() => {
//   //       navigate('/admin/blog/posts');
//   //     }, 1500);
//   //   } catch (error) {
//   //     console.error('Error saving post:', error);
//   //     showNotification('error', error.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
//   //   } finally {
//   //     setLoading(false);
//   //   }
//   // };
//   // Replace or modify the handleSubmit function in PostForm.js
//   // In handleSubmit function in PostForm.js
//   const handleSubmit = async (e, publishAfterSave = false) => {
//     e.preventDefault();
    
//     // Validate form before submission
//     const validationErrors = validateForm();
//     if (validationErrors.length > 0) {
//       showNotification('error', `Please fix the following errors: ${validationErrors.join(', ')}`);
//       return;
//     }
    
//     setLoading(true);
    
//     try {
//       const token = localStorage.getItem('token');
//       const method = isEditing ? 'PUT' : 'POST';
//       const url = isEditing 
//         ? `/api/blog/posts/${postId}`
//         : '/api/blog/posts';
      
//       // If publishing, override the status
//       const formData = {
//         ...postForm,
//         content: editorContent,
//         status: publishAfterSave ? 'published' : postForm.status
//       };

//       console.log('Submitting form data:', JSON.stringify(formData)); // Better debugging
      
//       const res = await fetch(url, {
//         method,
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${token}`
//         },
//         body: JSON.stringify(formData)
//       });
      
//       if (!res.ok) {
//         const errorText = await res.text();
//         console.error('Server response:', errorText);
//         const errorData = JSON.parse(errorText);
//         throw new Error(errorData.error || 'Failed to save post');
//       }
      
//       const data = await res.json();
      
//       // Update original post to reset dirty state
//       setOriginalPost(formData);
//       setIsDirty(false);
      
//       showNotification('success', `Post ${isEditing ? 'updated' : 'created'} successfully`);
      
//       // Redirect to posts list after short delay
//       setTimeout(() => {
//         navigate('/admin/blog/posts');
//       }, 1500);
//     } catch (error) {
//       console.error('Error saving post:', error);
//       showNotification('error', error.message || `Failed to ${isEditing ? 'update' : 'create'} post`);
//     } finally {
//       setLoading(false);
//     }
//   };
    

//   const handleFileUpload = async (file, type) => {
//     if (!file) return;
    
//     try {
//       const token = localStorage.getItem('token');
//       const formData = new FormData();
//       formData.append('file', file);
      
//       const res = await fetch('/api/blog/upload', {
//         method: 'POST',
//         headers: {
//           Authorization: `Bearer ${token}`
//         },
//         body: formData
//       });
      
//       if (!res.ok) {
//         const errorData = await res.json();
//         throw new Error(errorData.error || 'Failed to upload file');
//       }
      
//       const data = await res.json();
      
//       // Update form based on file type
//       if (type === 'cover') {
//         setPostForm(prev => ({
//           ...prev,
//           coverImage: data.filePath
//         }));
//       } else if (type === 'image') {
//         setPostForm(prev => ({
//           ...prev,
//           images: [...prev.images, data.filePath]
//         }));
//       } else if (type === 'video') {
//         setPostForm(prev => ({
//           ...prev,
//           videos: [...prev.videos, data.filePath]
//         }));
//       }
      
//       return data.filePath;
//     } catch (error) {
//       console.error('Error uploading file:', error);
//       showNotification('error', error.message || 'Failed to upload file');
//       return null;
//     }
//   };
  
//   const handleRemoveMedia = (type, index) => {
//     if (type === 'cover') {
//       setPostForm(prev => ({
//         ...prev,
//         coverImage: ''
//       }));
//     } else if (type === 'image') {
//       setPostForm(prev => ({
//         ...prev,
//         images: prev.images.filter((_, i) => i !== index)
//       }));
//     } else if (type === 'video') {
//       setPostForm(prev => ({
//         ...prev,
//         videos: prev.videos.filter((_, i) => i !== index)
//       }));
//     }
//   };
  
//   const showNotification = (type, message, duration = 5000) => {
//     setNotification({ type, message });
//     if (duration) {
//       setTimeout(() => setNotification(null), duration);
//     }
//   };
  
//   // Generate SEO fields from content
//   const generateSeoFields = () => {
//     // If title is empty, don't try to generate SEO fields
//     if (!postForm.title) {
//       showNotification('error', 'Please add a title first');
//       return;
//     }
    
//     // Generate SEO title if empty
//     if (!postForm.seoTitle) {
//       setPostForm(prev => ({
//         ...prev,
//         seoTitle: prev.title
//       }));
//     }
    
//     // Generate SEO description from excerpt or content
//     if (!postForm.seoDescription) {
//       let description = '';
      
//       if (postForm.excerpt) {
//         description = postForm.excerpt;
//       } else if (editorContent) {
//         // Strip HTML tags and get first 160 characters
//         const plainText = editorContent.replace(/<[^>]*>?/gm, '');
//         description = plainText.substring(0, 160).trim();
        
//         // Add ellipsis if truncated
//         if (plainText.length > 160) {
//           description += '...';
//         }
//       }
      
//       setPostForm(prev => ({
//         ...prev,
//         seoDescription: description
//       }));
//     }
    
//     // Generate keywords from title and content
//     if (!postForm.seoKeywords) {
//       // Extract key terms from title and content
//       const allText = `${postForm.title} ${editorContent.replace(/<[^>]*>?/gm, '')}`;
      
//       // Split into words, filter out short words and common stop words
//       const stopWords = ['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'of'];
//       const words = allText.toLowerCase()
//         .replace(/[^\w\s]/g, '')
//         .split(/\s+/)
//         .filter(word => word.length > 3 && !stopWords.includes(word));
      
//       // Count frequency
//       const wordCounts = {};
//       words.forEach(word => {
//         wordCounts[word] = (wordCounts[word] || 0) + 1;
//       });
      
//       // Get top 5-8 most frequent words
//       const keywords = Object.entries(wordCounts)
//         .sort((a, b) => b[1] - a[1])
//         .slice(0, 8)
//         .map(entry => entry[0])
//         .join(', ');
      
//       setPostForm(prev => ({
//         ...prev,
//         seoKeywords: keywords
//       }));
//     }
    
//     showNotification('success', 'SEO fields generated');
//   };
  
//   if (loadingPost) {
//     return (
//       <div className="bg-white rounded-lg shadow-md p-6">
//         <div className="flex justify-center items-center h-64">
//           <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
//           <p className="ml-2 text-gray-500">Loading post...</p>
//         </div>
//       </div>
//     );
//   }
  
//   return (
//     <div className="bg-white pt-4 rounded-lg shadow-md">
//       {notification && (
//         <NotificationBar
//           type={notification.type}
//           message={notification.message}
//           onClose={() => setNotification(null)}
//         />
//       )}
      
//       <div className="px-1 pr-6 py-4 border-b border-gray-200">
//         <div className="flex justify-between items-center">
//           <div className="flex items-center">
//             <button
//               onClick={() => {
//                 if (isDirty) {
//                   if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
//                     navigate('/admin/blog/posts');
//                   }
//                 } else {
//                   navigate('/admin/blog/posts');
//                 }
//               }}
//               className="text-gray-500 hover:text-gray-700"
//             >
//               <ArrowLeft className="h-5 w-5" />
//             </button>
//             <h2 className="text-xl font-semibold">
//               {isEditing ? 'Edit Post' : 'Create New Post'}
//             </h2>
//           </div>
          
//           <div className="flex items-center">
//             <span className="mr-2 text-sm text-gray-500">Status:</span>
//             <select
//               name="status"
//               value={postForm.status}
//               onChange={handleInputChange}
//               className="border border-gray-300 rounded-md text-sm py-1 px-2 focus:ring-blue-500 focus:border-blue-500"
//             >
//               <option value="draft">Draft</option>
//               <option value="published">Published</option>
//             </select>
//           </div>
//         </div>
        
//         <div className="flex justify-end mt-3">
//           <div className="flex items-center space-x-2">
//             <button
//               onClick={(e) => handleSubmit(e, false)}
//               disabled={loading}
//               className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
//             >
//               <Save className="h-4 w-4 mr-2" />
//               Save
//             </button>
            
//             <button
//               onClick={(e) => handleSubmit(e, true)}
//               disabled={loading}
//               className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
//             >
//               <CheckCircle className="h-4 w-4 mr-2" />
//               {postForm.status === 'published' ? 'Update' : 'Publish'}
//             </button>
            
//             <button
//               onClick={() => window.open(`/blog/${originalPost?.slug}`, '_blank')}
//               disabled={!isEditing || postForm.status !== 'published'}
//               className={`px-4 py-2 rounded-md flex items-center ${
//                 isEditing && postForm.status === 'published'
//                   ? 'bg-green-600 text-white hover:bg-green-700'
//                   : 'bg-gray-100 text-gray-400 cursor-not-allowed'
//               }`}
//             >
//               <Eye className="h-4 w-4 mr-2" />
//               View
//             </button>
//           </div>
//         </div>
//       </div>
      
//       {/* Post Form */}
//       <div className="p-6">
//         <form onSubmit={(e) => handleSubmit(e, false)}>
//           {/* Tabs */}
//           <div className="border-b border-gray-200 mb-6">
//             <div className="flex -mb-px space-x-8">
//               <button
//                 type="button"
//                 onClick={() => setActiveTab('content')}
//                 className={`pb-4 px-1 border-b-2 font-medium text-sm ${
//                   activeTab === 'content'
//                     ? 'border-blue-500 text-blue-600'
//                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//                 }`}
//               >
//                 Content
//               </button>
//               <button
//                 type="button"
//                 onClick={() => setActiveTab('media')}
//                 className={`pb-4 px-1 border-b-2 font-medium text-sm ${
//                   activeTab === 'media'
//                     ? 'border-blue-500 text-blue-600'
//                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//                 }`}
//               >
//                 Media
//               </button>
//               <button
//                 type="button"
//                 onClick={() => setActiveTab('seo')}
//                 className={`pb-4 px-1 border-b-2 font-medium text-sm ${
//                   activeTab === 'seo'
//                     ? 'border-blue-500 text-blue-600'
//                     : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//                 }`}
//               >
//                 SEO
//               </button>
//             </div>
//           </div>
          
//           {/* Content Tab */}
//           {activeTab === 'content' && (
//             <div className="space-y-6">
//               <div>
//                 <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
//                   Title <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   id="title"
//                   name="title"
//                   required
//                   value={postForm.title}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Post title"
//                 />
//                   <p className="mt-1 text-sm text-gray-500">
//                   {postForm.title.length} / 55 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
//                  Description
//                 </label>
//                 <textarea
//                   id="excerpt"
//                   name="excerpt"
//                   value={postForm.excerpt}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Brief description (shown in previews)"
//                 ></textarea>
//                 <p className="mt-1 text-sm text-gray-500">
//                   {postForm.excerpt.length} / 160 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
//                   Category <span className="text-red-500">*</span>
//                 </label>
//                 <select
//                   id="category"
//                   name="category"
//                   required
//                   value={postForm.category}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                 >
//                   <option value="">Select a category</option>
//                   {categories.map(category => (
//                     <option key={category._id} value={category._id}>
//                       {category.name}
//                     </option>
//                   ))}
//                 </select>
//               </div>
              
//               <div>
//                 <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
//                   Tags
//                 </label>
//                 <select
//                   id="tags"
//                   name="tags"
//                   multiple
//                   value={postForm.tags}
//                   onChange={handleTagsChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 h-32"
//                 >
//                   {tags.map(tag => (
//                     <option key={tag._id} value={tag._id}>
//                       {tag.name}
//                     </option>
//                   ))}
//                 </select>
//                 <p className="mt-1 text-sm text-gray-500">
//                   Hold Ctrl (or Command on Mac) to select multiple tags
//                 </p>
//               </div>

//               <div className="flex items-center mt-2">
//                 <input
//                   type="checkbox"
//                   id="featured"
//                   name="featured"
//                   checked={postForm.featured}
//                   onChange={(e) => setPostForm(prev => ({ ...prev, featured: e.target.checked }))}
//                   className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                 />
//                 <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
//                   Feature this post
//                 </label>
//               </div>
              

//               {/* AI Content Generation Button - Add this right above the RichTextEditor component */}
//               <div className="flex justify-end mb-2">
//                 <button
//                   type="button"
//                   onClick={generateAIContent}
//                   disabled={aiLoading}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
//                 >
//                   {aiLoading ? (
//                     <>
//                       <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
//                       Generating...
//                     </>
//                   ) : (
//                     <>
//                       <Sparkles className="h-4 w-4 mr-2" />
//                       Generate AI Content
//                     </>
//                   )}
//                 </button>
//               </div>

//               <div>
//                 <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
//                   Content <span className="text-red-500">*</span>
//                 </label>
//                 <RichTextEditor 
//                   initialContent={editorContent}
//                   onChange={handleEditorChange}
//                   placeholder="Write your post content here..."
//                 />
//               </div>
//             </div>
//           )}
          
//           {/* Media Tab */}
//           {activeTab === 'media' && (
//             <div className="space-y-6">
//               <div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-3">Cover Image</h3>
//                 <div className="border border-gray-300 rounded-md p-4">
//                   {postForm.coverImage ? (
//                     <div className="relative">
//                       <img
//                         src={postForm.coverImage}
//                         alt="Cover"
//                         className="w-full h-64 object-cover rounded-md"
//                       />
//                       <button
//                         type="button"
//                         onClick={() => handleRemoveMedia('cover')}
//                         className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
//                       >
//                         <X className="h-4 w-4" />
//                       </button>
//                     </div>
//                   ) : (
//                     <div className="flex flex-col items-center justify-center h-64 bg-gray-100 rounded-md">
//                       <Image className="h-16 w-16 text-gray-400 mb-2" />
//                       <p className="text-sm text-gray-500 mb-2">No cover image selected</p>
//                       <button
//                         type="button"
//                         onClick={() => coverImageInputRef.current?.click()}
//                         className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                       >
//                         <Upload className="h-4 w-4 mr-2" />
//                         Select Cover Image
//                       </button>
//                       <input
//                         ref={coverImageInputRef}
//                         type="file"
//                         accept="image/*"
//                         className="hidden"
//                         onChange={(e) => {
//                           if (e.target.files?.length) {
//                             handleFileUpload(e.target.files[0], 'cover');
//                           }
//                         }}
//                       />
//                     </div>
//                   )}
//                 </div>
//               </div>
              
//               <div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-3">Additional Images</h3>
//                 <div className="border border-gray-300 rounded-md p-4">
//                   {postForm.images.length > 0 ? (
//                     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
//                       {postForm.images.map((image, index) => (
//                         <div key={index} className="relative">
//                           <img
//                             src={image}
//                             alt={`Image ${index + 1}`}
//                             className="w-full h-32 object-cover rounded-md"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => handleRemoveMedia('image', index)}
//                             className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
//                           >
//                             <X className="h-4 w-4" />
//                           </button>
//                         </div>
//                       ))}
//                       <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                         <button
//                           type="button"
//                           onClick={() => imageInputRef.current?.click()}
//                           className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                         >
//                           <Plus className="h-4 w-4 mr-1" />
//                           Add Image
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                       <Image className="h-8 w-8 text-gray-400 mb-2" />
//                       <p className="text-sm text-gray-500 mb-2">No additional images</p>
//                       <button
//                         type="button"
//                         onClick={() => imageInputRef.current?.click()}
//                         className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                       >
//                         <Upload className="h-4 w-4 mr-1" />
//                         Add Images
//                       </button>
//                     </div>
//                   )}
//                   <input
//                     ref={imageInputRef}
//                     type="file"
//                     accept="image/*"
//                     className="hidden"
//                     onChange={(e) => {
//                       if (e.target.files?.length) {
//                         handleFileUpload(e.target.files[0], 'image');
//                       }
//                     }}
//                   />
//                 </div>
//               </div>
              
//               <div>
//                 <h3 className="text-lg font-medium text-gray-900 mb-3">Videos</h3>
//                 <div className="border border-gray-300 rounded-md p-4">
//                   {postForm.videos.length > 0 ? (
//                     <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
//                       {postForm.videos.map((video, index) => (
//                         <div key={index} className="relative">
//                           <video
//                             src={video}
//                             controls
//                             className="w-full h-32 rounded-md bg-black"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => handleRemoveMedia('video', index)}
//                             className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
//                           >
//                             <X className="h-4 w-4" />
//                           </button>
//                         </div>
//                       ))}
//                       <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                         <button
//                           type="button"
//                           onClick={() => videoInputRef.current?.click()}
//                           className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                         >
//                           <Plus className="h-4 w-4 mr-1" />
//                           Add Video
//                         </button>
//                       </div>
//                     </div>
//                   ) : (
//                     <div className="flex flex-col items-center justify-center h-32 bg-gray-100 rounded-md">
//                       <Film className="h-8 w-8 text-gray-400 mb-2" />
//                       <p className="text-sm text-gray-500 mb-2">No videos added</p>
//                       <button
//                         type="button"
//                         onClick={() => videoInputRef.current?.click()}
//                         className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
//                       >
//                         <Upload className="h-4 w-4 mr-1" />
//                         Add Video
//                       </button>
//                     </div>
//                   )}
//                   <input
//                     ref={videoInputRef}
//                     type="file"
//                     accept="video/*"
//                     className="hidden"
//                     onChange={(e) => {
//                       if (e.target.files?.length) {
//                         handleFileUpload(e.target.files[0], 'video');
//                       }
//                     }}
//                   />
//                 </div>
//               </div>
//             </div>
//           )}
          
//           {/* SEO Tab */}
//           {activeTab === 'seo' && (
//             <div className="space-y-6">
//               {/* <div className="flex justify-end">
//                 <button
//                   type="button"
//                   onClick={generateSeoFields}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
//                 >
//                   <CheckCircle className="h-4 w-4 mr-2" />
//                   Auto-generate SEO
//                 </button>
//               </div> */}
//               {/* SEO Tab AI Button - Replace the existing Auto-generate SEO button */}
//               <div className="flex justify-end space-x-2">
//                 <button
//                   type="button"
//                   onClick={generateSeoFields}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
//                 >
//                   <CheckCircle className="h-4 w-4 mr-2" />
//                   Auto-generate SEO
//                 </button>
//                 <button
//                   type="button"
//                   onClick={generateAISeo}
//                   disabled={aiLoading}
//                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
//                 >
//                   {aiLoading ? (
//                     <>
//                       <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
//                       Generating...
//                     </>
//                   ) : (
//                     <>
//                       <Wand className="h-4 w-4 mr-2" />
//                       AI SEO Generator
//                     </>
//                   )}
//                 </button>
//               </div>

//               <div>
//                 <label htmlFor="seoTitle" className="block text-sm font-medium text-gray-700 mb-1">
//                   SEO Title
//                 </label>
//                 <input
//                   type="text"
//                   id="seoTitle"
//                   name="seoTitle"
//                   value={postForm.seoTitle}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="SEO title (shown in search results)"
//                 />
//                 <p className="mt-1 text-sm text-gray-500">
//                   {postForm.seoTitle.length} / 60 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="seoDescription" className="block text-sm font-medium text-gray-700 mb-1">
//                   Meta Description
//                 </label>
//                 <textarea
//                   id="seoDescription"
//                   name="seoDescription"
//                   value={postForm.seoDescription}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Meta description (shown in search results)"
//                 ></textarea>
//                 <p className="mt-1 text-sm text-gray-500">
//                   {postForm.seoDescription.length} / 160 characters recommended
//                 </p>
//               </div>
              
//               <div>
//                 <label htmlFor="seoKeywords" className="block text-sm font-medium text-gray-700 mb-1">
//                   Meta Keywords
//                 </label>
//                 <input
//                   type="text"
//                   id="seoKeywords"
//                   name="seoKeywords"
//                   value={postForm.seoKeywords}
//                   onChange={handleInputChange}
//                   className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500"
//                   placeholder="Comma-separated keywords"
//                 />
//                 <p className="mt-1 text-sm text-gray-500">
//                   Less important for SEO nowadays, but still useful for organization
//                 </p>
//               </div>
              
//               <div className="border p-4 rounded-md bg-gray-50">
//                 <h4 className="font-medium text-sm text-gray-700 mb-2">SEO Preview</h4>
//                 <div className="border p-3 rounded-md bg-white">
//                   <div className="text-blue-600 text-lg truncate">
//                     {postForm.seoTitle || postForm.title || 'Post Title'}
//                   </div>
//                   <div className="text-green-600 text-sm truncate">
//                     yourdomain.com/blog/{postForm.title
//                       ? postForm.title
//                           .toLowerCase()
//                           .replace(/[^\w\s]/g, '')
//                           .replace(/\s+/g, '-')
//                       : 'post-slug'}
//                   </div>
//                   <div className="text-gray-600 text-sm mt-1">
//                     {postForm.seoDescription || 'Your meta description will appear here. It should be concise and engaging to encourage clicks.'}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
          
//           {/* Form submission buttons at bottom */}
//           <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end space-x-3">
//             <button
//               type="button"
//               onClick={() => {
//                 if (isDirty) {
//                   if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
//                     navigate('/admin/blog/posts');
//                   }
//                 } else {
//                   navigate('/admin/blog/posts');
//                 }
//               }}
//               className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
//             >
//               Cancel
//             </button>
            
//             <button
//               type="submit"
//               disabled={loading}
//               className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center"
//             >
//               {loading ? (
//                 <>
//                   <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
//                   Saving...
//                 </>
//               ) : (
//                 <>
//                   <Save className="h-4 w-4 mr-2" />
//                   {postForm.status === 'published' ? 'Update' : 'Save Draft'}
//                 </>
//               )}
//             </button>
            
//             {postForm.status !== 'published' && (
//               <button
//                 type="button"
//                 onClick={(e) => handleSubmit(e, true)}
//                 disabled={loading}
//                 className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center"
//               >
//                 {loading ? (
//                   <>
//                     <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
//                     Publishing...
//                   </>
//                 ) : (
//                   <>
//                     <CheckCircle className="h-4 w-4 mr-2" />
//                     Publish
//                   </>
//                 )}
//               </button>
//             )}
//           </div>
//         </form>
//       </div>
//       <style jsx="true">{`
//         button {
//           box-shadow: none !important;
//           border: none !important;
//           transform: none !important;
//         }
//        `}</style>
//     </div>
//   );
// };

// export default PostForm;




